import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import MFAVerificationModal from "../components/MFAVerificationModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthPage = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaLoginData, setMfaLoginData] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      
      // Check if MFA is required
      if (response.data.mfa_required) {
        setMfaLoginData({ email: loginData.email, password: loginData.password });
        setShowMfaModal(true);
      } else {
        onLogin(response.data.access_token, response.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSuccess = (token, user) => {
    setShowMfaModal(false);
    setMfaLoginData(null);
    setLoginData({ email: "", password: "" });
    onLogin(token, user);
  };

  const handleMfaCancel = () => {
    setShowMfaModal(false);
    setMfaLoginData(null);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
      toast.success("Password reset request submitted. Please contact your administrator.");
      setShowForgotPassword(false);
      setForgotPasswordEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit password reset request");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 flex items-center justify-center p-4">
      <div className="w-full max-w-md mx-auto space-y-8">
        {/* Centered Title */}
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-800">Financial Analysis Site</h1>
        </div>

        {/* Login Form */}
        <div className="w-full space-y-6">
          <Card className="shadow-xl border-slate-200">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-bold text-slate-800">Sign In</CardTitle>
              <CardDescription className="text-slate-600">
                Enter your credentials to access your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    placeholder="you@example.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="login-submit-button"
                  className="w-full bg-slate-800 hover:bg-slate-900"
                  disabled={isLoading}
                >
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>
              </form>
              
              {/* Forgot Password Link */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-slate-600 hover:text-slate-800 underline"
                >
                  Forgot your password?
                </button>
              </div>
              
              <p className="text-xs text-slate-500 text-center mt-4">
                Contact your administrator to create an account
              </p>
            </CardContent>
          </Card>

          {/* Logo Below */}
          <div className="flex justify-center items-end">
            <img 
              src="/rxDataMiningLogo.png" 
              alt="RxDataMining Logo" 
              className="h-16 w-auto"
            />
            <span className="text-[10px] text-slate-600 mb-2 -ml-1">©</span>
          </div>

          {/* Forgot Password Modal/Card */}
          {showForgotPassword && (
            <Card className="w-full max-w-md bg-white/80 backdrop-blur-md shadow-xl border border-slate-200 mt-4">
              <CardHeader className="text-center">
                <CardTitle className="text-xl font-bold text-slate-800">Reset Password</CardTitle>
                <CardDescription className="text-slate-600">
                  Enter your email and an administrator will reset your password
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email Address</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="you@example.com"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      className="flex-1 bg-slate-800 hover:bg-slate-900"
                      disabled={isLoading}
                    >
                      {isLoading ? "Submitting..." : "Request Reset"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotPasswordEmail("");
                      }}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* MFA Verification Modal */}
      {showMfaModal && mfaLoginData && (
        <MFAVerificationModal
          email={mfaLoginData.email}
          password={mfaLoginData.password}
          onSuccess={handleMfaSuccess}
          onCancel={handleMfaCancel}
        />
      )}
    </div>
  );
};

export default AuthPage;
